import { HttpErrorResponse } from '@angular/common/http';
import { createReducer, on } from '@ngrx/store';
import * as PriceListActions from './priceListv2.actions';
import * as _ from 'lodash';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { IRequestOptionsV2, } from '../request-options.v2.interface';
import { contractmanagement_api } from '@gridscale/gs-services/contractmanagement_api'; import { IMeta } from '../request-options.interface';

export const priceListFeatureKey = 'pricelistv2';




/******************************
 * List Adapter
 ******************************/
export const listAdapter: EntityAdapter<contractmanagement_api.PriceListListItemData> = createEntityAdapter<contractmanagement_api.PriceListListItemData>({
  selectId: a => a.id,
  sortComparer: false
});

// Extend List Entety State
export interface ListState extends EntityState<contractmanagement_api.PriceListListItemData> {
  listConfig: IRequestOptionsV2;
  loaded: boolean;
  loading: boolean;
  loadingNext: boolean;
  meta?: contractmanagement_api.Meta;
  links?: any;
  error?: Error | HttpErrorResponse;
}

// Define List Initial State
export const listAdapterInitialState = listAdapter.getInitialState({
  listConfig: {
    size: 20,
    page: 0
  },
  loaded: false,
  loading: false,
  loadingNext: false
}) as ListState;

/******************************
 * List Adapter
 ******************************/
export const entityAdapter: EntityAdapter<contractmanagement_api.PriceListDetailResponse> = createEntityAdapter<contractmanagement_api.PriceListDetailResponse>({
  selectId: a => a.data.id || '',
  sortComparer: false
});

// Extend List Entety State
export interface SingleState extends EntityState<contractmanagement_api.PriceListDetailResponse> {
  loading: { [key: string]: boolean };
  patching: { [key: string]: boolean };
  deleting: { [key: string]: boolean };
  error?: Error | HttpErrorResponse;
}

// Define List Initial State
export const entityAdapterInitialState = entityAdapter.getInitialState({
  loading: {},
  patching: {},
  deleting: {}
}) as SingleState;

/******************************
 * Price List Contracts Example
 ******************************/
export interface PriceListContracts {
  loaded: boolean;
  loading: boolean;
  loadingNext: boolean;
  deleting: boolean;
  patching: boolean;
  data: contractmanagement_api.PriceListContractsList;
  meta: IMeta
}

/******************************
 * Default States
 ******************************/
export interface State {
  list: ListState;
  single: SingleState;
  price_list_contracts: { [key: string]: PriceListContracts };
  error?: Error | HttpErrorResponse;
  defaultPriceListLoaded: boolean;
  defaultPriceList: contractmanagement_api.PriceListListItemData | undefined;
}

export const initialState: State = {
  list: listAdapterInitialState,
  single: entityAdapterInitialState,
  price_list_contracts: {},
  defaultPriceList: undefined,
  defaultPriceListLoaded: false
};

/***********************************************************
 * Reducers
 ***********************************************************/

/*******************************
 * Loading List
 ******************************/

export const reducer = createReducer(
  initialState,

  on(PriceListActions.loadPriceList, (state: State, action) => ({
    ...state,
    list: {
      ...state.list,
      loading: true
    }
  })),

  on(PriceListActions.loadPriceListSuccess, (state: State, action) => {
    return {
      ...state,
      list: listAdapter.setAll(action.result.data, {
        ...state.list,
        meta: action.result.meta,
        links: action.result.links,
        loading: false,
        loaded: true
      }),
      error: undefined
    };
  }),

  on(PriceListActions.loadPriceListFailure, (state: State, action) => ({
    ...state,
    loading: false,
    error: action.error
  })),

  /*******************************
   * Loading Next List
   ******************************/

  on(PriceListActions.loadPriceListNext, (state: State, action) => ({
    ...state,
    list: {
      ...state.list,
      loadingNext: true
    }
  })),

  on(PriceListActions.loadPriceListNextSuccess, (state: State, action) => {

    return {
      ...state,
      list: listAdapter.upsertMany(action.result.data, {
        ...state.list,
        meta: action.result.meta,
        links: action.result.links,
        loadingNext: false
      }),
      error: undefined
    };
  }),

  on(PriceListActions.loadDefaultPriceListSuccess, (state: State, action): State => {

    return {
      ...state,
      defaultPriceListLoaded: true,
      defaultPriceList: action.result.data[0]
    };
  }),

  /*******************************
   * Loading Single
   ******************************/

  on(PriceListActions.getPriceList, (state: State, action) => ({
    ...state,
    single: {
      ...state.single,
      loading: {
        ...state.single.loading,
        [action.price_list_uuid]: true
      }
    }
  })),

  on(PriceListActions.getPriceListSuccess, (state: State, action) => {

    return {
      ...state,
      single: entityAdapter.setOne(action.result, {
        ...state.single,
        loading: {
          ...state.single.loading,
          [action.price_list_uuid]: false
        }
      })
    };
  }),

  on(PriceListActions.getPriceListFailure, (state: State, action) => ({
    ...state,
    single: {
      ...state.single,
      loading: {
        ...state.single.loading,
        [action.price_list_uuid]: false
      },
      error: action.error
    }
  })),

  /*******************************
   * Deleting Single
   ******************************/

  on(PriceListActions.deletePriceList, (state, action) => ({
    ...state,
    single: {
      ...state.single,
      deleting: {
        ...state.single.deleting,
        [action.price_list_uuid]: true
      }
    }
  })),

  on(PriceListActions.deletePriceListSuccess, (state, action) => ({
    ...state,
    list: listAdapter.removeOne(action.price_list_uuid, state.list),
    single: entityAdapter.removeOne(action.price_list_uuid, {
      ...state.single,
      deleting: {
        ...state.single.deleting,
        [action.price_list_uuid]: false
      }
    })
  })),

  on(PriceListActions.deletePriceListFailure, (state, action) => ({
    ...state,
    single: {
      ...state.single,
      deleting: {
        ...state.single.deleting,
        [action.price_list_uuid]: false
      },
      error: action.error
    }
  })),

  /*******************************
   * Patching Single
   ******************************/

  on(PriceListActions.patchPriceList, PriceListActions.patchPriceListPrices, PriceListActions.addAccountsToPriceList, (state, action) => ({
    ...state,
    single: {
      ...state.single,
      patching: {
        ...state.single.patching,
        [action.price_list_uuid]: true
      }
    }
  })),

  on(PriceListActions.patchPriceListSuccess, PriceListActions.patchPriceListPricesSuccess, PriceListActions.addContractsToPriceListSuccess, (state, action) => ({
    ...state,
    single: {
      ...state.single,
      patching: {
        ...state.single.patching,
        [action.price_list_uuid]: false
      }
    }
  })),

  on(PriceListActions.patchPriceListFailure, PriceListActions.patchPriceListPricesFailure, PriceListActions.addContractsToPriceListFailure, (state, action) => ({
    ...state,
    single: {
      ...state.single,
      patching: {
        ...state.single.patching,
        [action.price_list_uuid]: false
      },
      error: action.error
    }
  })),

  on(PriceListActions.patchPriceList, (state, action) => ({
    ...state,
    single: {
      ...state.single,
      patching: {
        ...state.single.patching,
        [action.price_list_uuid]: true
      }
    }
  })),

  /*******************************
   * PriceList Contracts
   ******************************/

  on(PriceListActions.getPriceListContracts, (state, action) => ({
    ...state,
    price_list_contracts: {
      ...state.price_list_contracts,
      [action.price_list_uuid]: {
        ...state.price_list_contracts[action.price_list_uuid],
        deleting: false,
        loaded: false,
        loading: true,
        patching: false
      }
    }
  })),

  on(PriceListActions.getPriceListContractsSuccess, (state, action) => ({
    ...state,
    price_list_contracts: {
      ...state.price_list_contracts,
      [action.price_list_uuid]: {
        ...state.price_list_contracts[action.price_list_uuid],
        loaded: true,
        loading: false,
        data: action.result.data,
        meta: {
          count: action.result.meta.count,
          limit: action.result.meta.limit,
          page: action.result.meta.page,
          total: action.result.meta.total,
          offset: action.result.meta.page * action.result.meta.limit
        }
      }
    }
  })),

  on(PriceListActions.getPriceListContractsFailure, (state, action) => ({
    ...state,
    price_list_contracts: {
      ...state.price_list_contracts,
      [action.price_list_uuid]: {
        ...state.price_list_contracts[action.price_list_uuid],
        loaded: false,
        loading: false
      }
    }
  })),

  on(PriceListActions.getPriceListContractsNext, (state, action) => ({
    ...state,
    price_list_contracts: {
      ...state.price_list_contracts,
      [action.price_list_uuid]: {
        ...state.price_list_contracts[action.price_list_uuid],
        loadingNext: true
      }
    }
  })),

  on(PriceListActions.getPriceListContractsNextSuccess, (state, action) => ({
    ...state,
    price_list_contracts: {
      ...state.price_list_contracts,
      [action.price_list_uuid]: {
        ...state.price_list_contracts[action.price_list_uuid],
        loaded: true,
        loading: false,
        loadingNext: false,
        data: [...state.price_list_contracts[action.price_list_uuid].data, ...action.result.data],
        meta: action.result.meta
      }
    }
  })),

  on(PriceListActions.getPriceListContractsNextFailure, (state, action) => ({
    ...state,
    price_list_contracts: {
      ...state.price_list_contracts,
      [action.price_list_uuid]: {
        ...state.price_list_contracts[action.price_list_uuid],
        loadingNext: false
      }
    }
  }))


);
